import * as yup from 'yup'
import {
  CourseLevel,
  DiscountTypeOptions,
} from '../../core/common/data/json/courses'
import { getCategories } from '../../api/courses'
import dayjs from 'dayjs'
import {
  normalizeLeadingInteger,
  normalizeMaterialJpl,
} from '../../utils/numberNormalizer'
import { setAdminFavoriteOverride } from '../../utils/favoriteStore'

// --- Step 1: Course Information ---
export const contentInformationFieldsConfig = {
  title: {
    label: 'Judul Kontent',
    type: 'text',
    required: true,
    placeholder: 'Judul Konten',
    schema: yup.string().required('Judul konten is required'),
  },
  category_id: {
    label: 'Kategori',
    type: 'select',
    required: true,
    placeholder: 'Kategori',
    options: [],
    isSearchableAndInfinite: true,
    queryKeyBase: 'categories',
    apiFn: (params: any) => getCategories({ ...params, type: 'cbt' }),
    schema: yup.string().required('Kategori is required'),
  },
  course_level: {
    label: 'Level',
    type: 'select',
    required: true,
    placeholder: 'level',
    options: CourseLevel,
    schema: yup.string().required('Level is required'),
  },
  max_student: {
    label: 'Kuota',
    type: 'number',
    required: true,
    placeholder: 'e.g., 50',
    schema: yup
      .number()
      .required('Kuota is required')
      .positive('Must be a positive number')
      .integer('Must be an integer')
      .typeError('Must be a number'),
  },
  duration: {
    label: 'Durasi',
    type: 'time',
    required: true,
    placeholder: 'Atur durasi',
    schema: yup
      .string()
      .required('Durasi is required')
      .typeError('Durasi tidak valid'),
  },
  min_passing_score: {
    label: 'Minimum Passing Score',
    type: 'number',
    required: true,
    placeholder: 'Atur Min Score',
    schema: yup
      .string()
      .required('Minimum Passing Score is required')
      .typeError('Minimum Passing Score tidak valid'),
  },
  max_attempt: {
    label: 'Jumlah maksimal pengerjaan Try Out',
    type: 'number',
    required: true,
    placeholder: 'Maksimal Pengerjaan',
    schema: yup
      .string()
      .required('Jumlah maksimal pengerjaan Try Out is required')
      .typeError('Jumlah maksimal pengerjaan Try Out tidak valid'),
  },
  short_description: {
    label: 'Deskripsi Singkat',
    type: 'text',
    required: true,
    placeholder: 'Desktipsi Singkat',
    schema: yup
      .string()
      .required('Desktipsi Singkat is required')
      .max(255, 'Desktipsi Singkat must not exceed 255 characters'),
  },
  is_favorite: {
    label: 'Tandai Kelas Ini Sebagai Terfavorit (Best Seller)',
    type: 'checkbox',
    required: false,
    placeholder: '',
    schema: yup.boolean().optional().default(false),
  },
  description: {
    label: 'Deskripsi',
    type: 'richtext',
    required: true,
    placeholder: 'Deskripsi',
    schema: yup.string().required('Deskripsi is required'),
  },
  studied: {
    label: 'Apa yang akan dipelajari',
    type: 'array',
    required: true,
    placeholder: 'Apa yang akan dipelajari',
    schema: yup.array(
      yup.string().required('Apa yang akan dipelajari is required'),
    ),
  },
  thumbnail: {
    label: 'Thumbnail',
    type: 'file',
    required: true,
    placeholder: 'Upload image',
    schema: yup
      .mixed()
      .test('fileRequired', 'Thumbnail is required', (value: any) => {
        if (Array.isArray(value)) return value.length > 0
        return !!value
      })
      .test(
        'fileType',
        'Unsupported File Format (only JPG, JPEG, PNG)',
        (value: any) => {
          const file = value?.[0]
          if (!file && typeof file !== 'string') {
            return true
          }

          if (typeof file === 'string')
            return ['jpeg', 'png', 'jpg'].includes(value.split('.')[1])
          return ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
        },
      )
      .test('fileSize', 'File too large (max 5MB)', (value: any) => {
        const file = value?.[0]
        if (!file && typeof file !== 'string') return true
        if (typeof file === 'string') return true

        return file.size <= 5 * 1024 * 1024
      }),
  },
  poster: {
    label: 'Poster',
    type: 'file',
    required: true,
    placeholder: 'Upload image',
    schema: yup
      .mixed()
      .test('fileRequired', 'Poster is required', (value: any) => {
        if (Array.isArray(value)) return value.length > 0
        return !!value
      })
      .test(
        'fileType',
        'Unsupported File Format (only JPG, JPEG, PNG)',
        (value: any) => {
          const file = value?.[0]
          if (!file && typeof file !== 'string') {
            return true
          }

          if (typeof file === 'string')
            return ['jpeg', 'png', 'jpg'].includes(value.split('.')[1])
          return ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
        },
      )
      .test('fileSize', 'File too large (max 5MB)', (value: any) => {
        const file = value?.[0]
        if (!file && typeof file !== 'string') return true
        if (typeof file === 'string') return true

        return file.size <= 5 * 1024 * 1024
      }),
  },
  included_course_ids: {
    label: 'Included Courses (CBT/Other Courses)',
    type: 'multi-select',
    required: false,
    placeholder: 'Pilih course yang otomatis terdaftar',
    schema: yup.array().of(yup.number().required()).nullable(),
  },
}

// --- Step 2: Additional Information ---
export const additionalInformationFieldsConfig = {
  questions: {
    label: 'Question & Answer',
    type: 'array',
    required: true,
    placeholder: 'Question & Answer',
    schema: yup
      .array(
        yup.object().shape({
          question: yup.string().required('Question is required'),
          discussion: yup.string().nullable(),
          normal_lab: yup.string().nullable(),
          answers: yup
            .array(
              yup.object().shape({
                choice: yup.string().required('Choice is required'),
                is_true: yup.boolean(),
              }),
            )
            .min(1, 'Minimal satu jawaban wajib diisi')
            .test(
              'one-correct-answer',
              'Setidaknya satu jawaban harus benar',
              (value) => {
                if (!value || value.length === 0) return true
                return value.some((answer) => answer.is_true)
              },
            ),
        }),
      )
      .min(1, 'Minimal satu pertanyaan wajib ditambahkan')
      .required('Question & Answer section is required.'),
  },
  certificate_event_dates: {
    label: 'Tanggal Pelaksanaan Sertifikat',
    type: 'textarea',
    required: false,
    placeholder: 'Contoh: Held on 30, 31 May and 1 June 2026',
    schema: yup.string().nullable().optional(),
  },
  certificate_jpl_total: {
    label: 'Total JPL',
    type: 'text',
    required: false,
    placeholder: 'Contoh: 12 JP',
    schema: yup.string().nullable(),
  },
  certificate_jpl_note: {
    label: 'Catatan JPL',
    type: 'text',
    required: false,
    placeholder: 'Contoh: *) 1 JPL: 45 minutes',
    schema: yup.string().nullable(),
  },
  certificate_materials: {
    label: 'Materi Sertifikat',
    type: 'certificate_materials',
    required: false,
    placeholder:
      'Format per baris: Materi | JPL | Narasumber\nContoh: Materi A | 2 JP | apt. Nama Narasumber',
    schema: yup.string().nullable(),
  },
  certificate_location: {
    label: 'Lokasi Sertifikat',
    type: 'text',
    required: false,
    placeholder: 'Contoh: Bandar Lampung',
    defaultValue: 'Bandar Lampung',
    schema: yup.string().nullable(),
  },
}

// --- Step 3: Pricing ---
export const pricingFieldsConfig = {
  price: {
    label: 'Price',
    type: 'number',
    required: true,
    placeholder: 'e.g., 250000 or 0 for free',
    schema: yup
      .number()
      .required('Price is required')
      .min(0, 'Price cannot be negative')
      .typeError('Must be a number'),
  },
  discount_type: {
    label: 'Discount Type',
    type: 'select',
    required: true,
    placeholder: 'Discount',
    options: DiscountTypeOptions,
    schema: yup.string().required('Discount type is required'),
  },
  discount: {
    label: 'Discount Value (Optional)',
    type: 'number',
    required: false,
    placeholder: 'e.g., 50 (for percentage) or 50000 (for amount)',
    schema: yup
      .number()
      .typeError('Masukan 0 jika tidak ingin ada diskon')
      .min(0, 'Nilai diskon minimal 0')
      .nullable()
      // .when('discount_type', {
      //   is: (val: string) => val !== 'NONE',
      //   then: (schema) => schema.required("Discount value is required when discount type is not 'None'").positive("Discount value must be positive").typeError("Must be a number"),
      //   otherwise: (schema) => schema.nullable(),
      // })
      // Add validation for percentage vs amount if needed, e.g., percentage <= 100
      .test(
        'discount-percentage-max',
        'Percentage cannot exceed 100%',
        function (value) {
          const { discount_type } = this.parent
          if (
            discount_type === 'PERCENTAGE' &&
            typeof value === 'number' &&
            value > 100
          ) {
            return this.createError({
              message: 'Percentage cannot exceed 100%',
            })
          }
          return true
        },
      ),
  },
  cross_sell: {
    label: 'List Product Cross Sell',
    type: 'array',
    placeholder: 'Cross Sell',
    required: false,
    options: [],
    schema: yup
      .array(
        yup.object().shape({
          cross_course_id: yup
            .number()
            .required('Cross-sell course ID is required'),
          note: yup.string().nullable(),
          price: yup
            .number()
            .required('Price is required')
            .typeError('Price must be a number'),
        }),
      )
      .nullable()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          try {
            const parsed = JSON.parse(originalValue)
            return Array.isArray(parsed) ? parsed : null
          } catch {
            return null
          }
        }
        return value
      }),
  },
}

export const basicInformationSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(contentInformationFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

// export const courseContentSchema = yup.object().shape(
//   Object.fromEntries(
//     Object.entries(contentFieldsConfig).map(([key, val]) => [key, val.schema])
//   )
// );

export const additionalInformationSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(additionalInformationFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const pricingSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(pricingFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const fullCreateSchema = yup.object().shape({
  ...basicInformationSchema.fields,
  // ...courseContentSchema.fields,
  ...additionalInformationSchema.fields,
  ...pricingSchema.fields,
})

export type BasicInformationFormValues = yup.InferType<
  typeof basicInformationSchema
>
// export type ContentFormValues = yup.InferType<typeof ContentSchema>;
export type AdditionalInformationFormValues = yup.InferType<
  typeof additionalInformationSchema
>
export type PricingFormValues = yup.InferType<typeof pricingSchema>

export type CreateFormValues = BasicInformationFormValues &
  // ContentFormValues &
  AdditionalInformationFormValues &
  PricingFormValues

export function getInitialValuesFromConfig(
  config: Record<string, any>,
): Record<string, any> {
  return Object.keys(config).reduce((acc, key) => {
    const field = config[key]
    if (field.defaultValue !== undefined) {
      acc[key] = field.defaultValue
    } else if (field.type === 'checkbox') {
      acc[key] = false
    } else if (field.type === 'file') {
      acc[key] = null
    } else if (field.type === 'array' || field.type === 'chips') {
      acc[key] = []
    } else if (field.type === 'multi-select') {
      acc[key] = []
    } else if (field.type === 'number') {
      acc[key] = ''
    } else if (field.type === 'richtext') {
      acc[key] = ''
    } else if (field.type === 'radio') {
      acc[key] = field.options?.[0]?.value || ''
    } else {
      acc[key] = null
    }
    return acc
  }, {} as Record<string, any>)
}

export function parseCertificateMaterials(value: any) {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'string') return []

  const trimmedValue = value.trim()
  if (!trimmedValue) return []

  if (trimmedValue.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmedValue)
      if (Array.isArray(parsed)) return parsed
    } catch {
      // Fall back to the pipe-separated format below.
    }
  }

  return trimmedValue
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [topic = '', jpl = '', speaker = ''] = line
        .split('|')
        .map((part) => part.trim())

      return { topic, jpl, speaker }
    })
    .filter((item) => item.topic || item.jpl || item.speaker)
}

export function formatCertificateMaterials(value: any) {
  let materials = value

  if (typeof value === 'string') {
    const trimmedValue = value.trim()

    if (trimmedValue.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmedValue)
        if (Array.isArray(parsed)) {
          materials = parsed
        }
      } catch {
        return value || ''
      }
    }
  }

  if (!Array.isArray(materials)) return materials || ''

  return materials
    .map((item) =>
      [item?.topic || '', item?.jpl || '', item?.speaker || ''].join(' | '),
    )
    .join('\n')
}

// Initial values for each step
export const initialValuesStep1 = getInitialValuesFromConfig(
  contentInformationFieldsConfig,
)
// export const initialValuesStep2 = getInitialValuesFromConfig(contentFieldsConfig);
export const initialValuesStep2 = getInitialValuesFromConfig(
  additionalInformationFieldsConfig,
)
export const initialValuesStep3 = getInitialValuesFromConfig(
  pricingFieldsConfig,
)

export const initialFullFormValues: CreateFormValues = {
  ...initialValuesStep1,
  // ...initialValuesStep2,
  ...initialValuesStep2,
  ...initialValuesStep3,
}

// --- Function to transform Formik values into the desired API payload structure ---
export function transformPayload(values: any, originalValues?: any): FormData {
  const hour = dayjs(values.duration as any).hour() || 0
  const minute = dayjs(values.duration as any).minute() || 0

  const totalMs: number = (hour * 60 + minute) * 60 * 1000

  const formData = new FormData()

  const safeStr = (val: any): string => {
    if (val === null || val === undefined) return ''
    if (typeof val === 'object' && !Array.isArray(val)) return ''
    return String(val)
  }

  // Step 1 Fields
  formData.append('title', safeStr(values.title))
  formData.append('category_id', safeStr(values.category_id))
  formData.append('course_level', safeStr(values.course_level))
  formData.append('max_student', normalizeLeadingInteger(values.max_student))

  formData.append(
    'min_passing_score',
    normalizeLeadingInteger(values.min_passing_score),
  )
  formData.append('max_attempt', normalizeLeadingInteger(values.max_attempt))

  if (values.id || values.course_id) {
    formData.append('_method', 'PUT')
  }
  formData.append('short_description', safeStr(values.short_description))
  const isFav =
    values.is_favorite === true ||
    values.is_favorite === 1 ||
    values.is_favorite === '1' ||
    values.is_favorite === 'true'

  if (values.id || values.course_id) {
    setAdminFavoriteOverride(values.id || values.course_id, isFav)
  }

  const favValStr = isFav ? '1' : '0'
  formData.append('is_favorite', favValStr)
  formData.append('is_favourite', favValStr)
  formData.append('is_bestseller', favValStr)
  formData.append('is_best_seller', favValStr)
  formData.append('is_featured', favValStr)
  formData.append('is_top', favValStr)
  formData.append('is_popular', favValStr)
  formData.append('favorite', favValStr)
  formData.append('favourite', favValStr)
  formData.append('bestseller', favValStr)
  formData.append('badge', isFav ? 'bestseller' : '')
  formData.append('description', safeStr(values.description))
  formData.append(
    'included_course_ids',
    JSON.stringify(values.included_course_ids || []),
  )
  formData.append('studied', JSON.stringify(values.studied || []))
  if (values.certificate_event_dates !== undefined) {
    formData.append('certificate_event_dates', safeStr(values.certificate_event_dates))
  }
  if (values.certificate_jpl_total !== undefined) {
    formData.append(
      'certificate_jpl_total',
      normalizeLeadingInteger(values.certificate_jpl_total || ''),
    )
  }
  if (values.certificate_jpl_note !== undefined) {
    formData.append('certificate_jpl_note', safeStr(values.certificate_jpl_note))
  }
  if (values.certificate_materials !== undefined) {
    const parsedMaterials = parseCertificateMaterials(values.certificate_materials)
    const certificateMaterials = parsedMaterials.length
      ? parsedMaterials
      : (values.studied || []).map((topic: string) => ({
          topic,
          jpl: '',
          speaker: '',
        }))
    const normalizedCertificateMaterials = certificateMaterials.map((item: any) => ({
      ...item,
      jpl: normalizeMaterialJpl(item.jpl || ''),
    }))

    formData.append(
      'certificate_materials',
      JSON.stringify(normalizedCertificateMaterials),
    )
  }
  if (values.certificate_location !== undefined) {
    formData.append('certificate_location', safeStr(values.certificate_location))
  }

  // Handle thumbnail
  if (values?.thumbnail && typeof values.thumbnail !== 'string') {
    formData.append('thumbnail', values.thumbnail[0] || values.thumbnail)
  }

  // Handle poster
  if (values?.poster && typeof values.poster !== 'string') {
    formData.append('poster', values.poster[0] || values.poster)
  }

  // Step 2 Fields
  if (values.questions && Array.isArray(values.questions)) {
    const questionsJSON = values.questions.map((q: any) => ({
      ...(q.id ? { id: q.id } : {}),
      question: safeStr(q.question),
      discussion: safeStr(q.discussion).trim() || '-',
      normal_lab: safeStr(q.normal_lab),
      answers: (q.answers || []).map((a: any) => ({
        ...(a.id ? { id: a.id } : {}),
        choice: safeStr(a.choice),
        is_true: a.is_true ? 'true' : 'false',
      })),
    }))
    formData.append('questions', JSON.stringify(questionsJSON))

    values.questions.forEach((q: any, i: number) => {
      if (q.image && q.image instanceof File) {
        formData.append(`questions[${i}][image]`, q.image)
      }
    })
  } else {
    formData.append('questions', JSON.stringify([]))
  }

  // Step 3 Fields
  formData.append('price', safeStr(values.price || 0))
  formData.append('discount_type', safeStr(values.discount_type || 'NONE'))
  formData.append('cross_sell', JSON.stringify(values.cross_sell || []))
  if (
    values.discount !== null &&
    values.discount !== undefined &&
    values.discount !== ''
  ) {
    formData.append('discount', safeStr(values.discount))
  }

  formData.append('type', 'CBT')

  return formData
}
